// frontend/src/actions/cartActions.js

import * as actionTypes from "../constants/cartConstants";
import api from "../utils/axiosInstance";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";

const CART_BASE_URL = "/cart";

const VALID_PAYMENT_METHODS = new Set(["stripe", "paypal"]);
const VALID_FULFILLMENT_METHODS = new Set(["delivery", "pickup"]);

const STOCK_REFRESH_CODES = new Set([
  "CART_CONFLICT",
  "INSUFFICIENT_STOCK",
  "INSUFFICIENT_SHARED_STOCK",
  "MAX_IN_CART",
  "OUT_OF_STOCK",
  "REQUESTED_QTY_EXCEEDS_AVAILABLE",
  "SHARED_STOCK_EXHAUSTED_IN_CART",
  "SHARED_STOCK_BLOCKED_BY_CART",
  "UNAVAILABLE",
  "PRICE_CHANGED",
]);

const STOCK_REFRESH_STATUSES = new Set([
  "out_of_stock",
  "max_in_cart",
  "requested_qty_exceeds_available",
  "shared_stock_exhausted_in_cart",
  "shared_stock_blocked_by_cart",
  "unavailable",
]);

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function encodePath(value = "") {
  return encodeURIComponent(safeText(value));
}

function normalizeQty(qty = 1) {
  const number = Math.floor(Number(qty));

  if (!Number.isFinite(number) || number < 1) return 1;

  return number;
}

function toNonNegativeInteger(value, fallback = 0) {
  const number = Math.floor(Number(value));

  if (!Number.isFinite(number) || number < 0) return fallback;

  return number;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function getResponseHeader(error = {}, name = "") {
  const headers = error?.response?.headers || {};
  const target = normalizeLower(name);

  const key = Object.keys(headers || {}).find(
    (headerName) => normalizeLower(headerName) === target,
  );

  return safeText(key ? headers[key] : "");
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = normalizeLower(value);

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function containsSensitiveCustomerCopy(value = "") {
  const text = normalizeLower(value);

  return (
    text.includes("secret") ||
    text.includes("token") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("mongo") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token")
  );
}

function looksLikeRawProviderCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

function fallbackMessageForStockStatus(status = "", code = "") {
  const normalizedStatus = normalizeLower(status);
  const normalizedCode = normalizeUpper(code);

  if (normalizedStatus === "max_in_cart" || normalizedCode === "MAX_IN_CART") {
    return "This item is already in your basket.";
  }

  if (
    normalizedStatus === "shared_stock_exhausted_in_cart" ||
    normalizedCode === "SHARED_STOCK_EXHAUSTED_IN_CART"
  ) {
    return "This cut is already in your basket.";
  }

  if (
    normalizedStatus === "shared_stock_blocked_by_cart" ||
    normalizedCode === "SHARED_STOCK_BLOCKED_BY_CART" ||
    normalizedCode === "INSUFFICIENT_SHARED_STOCK"
  ) {
    return "Choose another cut from this shared roll.";
  }

  if (
    normalizedStatus === "requested_qty_exceeds_available" ||
    normalizedCode === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    normalizedCode === "INSUFFICIENT_STOCK"
  ) {
    return "The selected quantity is no longer available.";
  }

  if (
    normalizedStatus === "out_of_stock" ||
    normalizedCode === "OUT_OF_STOCK"
  ) {
    return "This item is sold out.";
  }

  if (normalizedStatus === "unavailable" || normalizedCode === "UNAVAILABLE") {
    return "This item is currently unavailable.";
  }

  if (normalizedCode === "PRICE_CHANGED") {
    return "A price changed in your basket. Please review before checkout.";
  }

  return "";
}

function customerSafeText(value = "", fallback = "") {
  const text = safeText(value);

  if (!text) return safeText(fallback);
  if (containsSensitiveCustomerCopy(text)) return safeText(fallback);
  if (looksLikeRawProviderCode(text)) return safeText(fallback);

  return containsUnsafeCustomerStockCopy(text) ? safeText(fallback) : text;
}

function buildClientValidationError(
  message,
  code,
  statusCode = 400,
  issues = [],
) {
  return {
    message: customerSafeText(message, "Please check your basket."),
    code,
    status: statusCode,
    statusCode,
    issues: Array.isArray(issues) ? issues : [],
  };
}

function normalizeErrorIssue(issue = {}) {
  const code = normalizeUpper(issue?.code);
  const stockStatus = normalizeLower(issue?.stockStatus || issue?.status);
  const fallbackMessage = fallbackMessageForStockStatus(stockStatus, code);

  const remainingAddableQty = toNonNegativeInteger(
    issue?.remainingAddableQty ?? issue?.maxAddableQty ?? 0,
    0,
  );

  const maxAddableQty = toNonNegativeInteger(
    issue?.maxAddableQty ?? issue?.remainingAddableQty ?? remainingAddableQty,
    0,
  );

  const statusReason = customerSafeText(
    issue?.statusReason ||
      issue?.stockStatusReason ||
      issue?.unavailableReason ||
      issue?.disabledReason ||
      issue?.message ||
      "",
    fallbackMessage,
  );

  return {
    ...issue,

    code,
    stockStatus,
    stockSeverity: normalizeLower(issue?.stockSeverity),

    message: customerSafeText(issue?.message, statusReason || fallbackMessage),
    statusReason,
    stockStatusReason: statusReason,
    unavailableReason: statusReason,
    disabledReason: statusReason,

    productId: safeText(issue?.productId),
    variantSku: normalizeUpper(issue?.variantSku),
    variantType: normalizeLower(issue?.variantType),

    remainingAddableQty,
    maxAddableQty,
    alreadyInCartQty: toNonNegativeInteger(issue?.alreadyInCartQty ?? 0, 0),
  };
}

function normalizeErrorPayload(error, fallback = USER_FACING_FALLBACK) {
  const status = Number(error?.response?.status || error?.statusCode || 0);
  const data = error?.response?.data || {};
  const fallbackApiMessage = getApiErrorMessage(error, { fallback });

  const issues = (
    Array.isArray(data?.issues)
      ? data.issues
      : Array.isArray(error?.issues)
        ? error.issues
        : []
  ).map(normalizeErrorIssue);

  const firstIssue = issues[0] || {};

  const code = normalizeUpper(
    firstDefined(
      data?.code,
      error?.code,
      error?.errorCode,
      firstIssue?.code,
      status === 409 ? "CART_CONFLICT" : "",
    ),
  );

  const stockStatus = normalizeLower(
    firstDefined(
      data?.stockStatus,
      data?.status,
      error?.stockStatus,
      error?.status,
      firstIssue?.stockStatus,
      "",
    ),
  );

  const stockSeverity = normalizeLower(
    firstDefined(
      data?.stockSeverity,
      error?.stockSeverity,
      firstIssue?.stockSeverity,
      "",
    ),
  );

  const fallbackStockMessage = fallbackMessageForStockStatus(stockStatus, code);

  const statusReason = customerSafeText(
    firstDefined(
      data?.statusReason,
      data?.stockStatusReason,
      data?.unavailableReason,
      data?.disabledReason,
      error?.statusReason,
      error?.stockStatusReason,
      error?.unavailableReason,
      error?.disabledReason,
      firstIssue?.statusReason,
      "",
    ),
    fallbackStockMessage,
  );

  const remainingAddableQty = toNonNegativeInteger(
    firstDefined(
      data?.remainingAddableQty,
      data?.maxAddableQty,
      error?.remainingAddableQty,
      error?.maxAddableQty,
      firstIssue?.remainingAddableQty,
      firstIssue?.maxAddableQty,
      0,
    ),
    0,
  );

  const maxAddableQty = toNonNegativeInteger(
    firstDefined(
      data?.maxAddableQty,
      data?.remainingAddableQty,
      error?.maxAddableQty,
      error?.remainingAddableQty,
      firstIssue?.maxAddableQty,
      firstIssue?.remainingAddableQty,
      remainingAddableQty,
    ),
    0,
  );

  const message = customerSafeText(
    firstDefined(
      data?.customerMessage,
      data?.message,
      error?.normalizedMessage,
      firstIssue?.message,
    firstIssue?.statusReason,
    statusReason,
    "",
  ),
    fallbackStockMessage || fallbackApiMessage,
  );

  return {
    message: message || fallbackApiMessage,

    code,
    status,

    stockStatus,
    stockSeverity,
    statusReason,

    productId: safeText(
      firstDefined(
        data?.productId,
        error?.productId,
        firstIssue?.productId,
        "",
      ),
    ),

    variantSku: normalizeUpper(
      firstDefined(
        data?.variantSku,
        error?.variantSku,
        firstIssue?.variantSku,
        "",
      ),
    ),

    variantType: normalizeLower(
      firstDefined(
        data?.variantType,
        error?.variantType,
        firstIssue?.variantType,
        "",
      ),
    ),

    remainingAddableQty,
    maxAddableQty,

    alreadyInCartQty: toNonNegativeInteger(
      firstDefined(
        data?.alreadyInCartQty,
        error?.alreadyInCartQty,
        firstIssue?.alreadyInCartQty,
        0,
      ),
      0,
    ),

    issues,

    requestId: safeText(
      data?.requestId ||
        error?.requestId ||
        getResponseHeader(error, "x-request-id") ||
        "",
    ),

    retryAfterSeconds: Number(
      data?.retryAfterSeconds ||
        error?.retryAfterSeconds ||
        getResponseHeader(error, "retry-after") ||
        0,
    ),

    raw: {
      code,
      status,
      requestId: safeText(
        data?.requestId ||
          error?.requestId ||
          getResponseHeader(error, "x-request-id") ||
          "",
      ),
    },
  };
}

function shouldRefreshAfterFailure(payload = {}) {
  const status = Number(payload?.status || 0);
  const code = normalizeUpper(payload?.code);
  const stockStatus = normalizeLower(payload?.stockStatus);

  if (status === 409) return true;
  if (STOCK_REFRESH_CODES.has(code)) return true;
  if (STOCK_REFRESH_STATUSES.has(stockStatus)) return true;

  return (payload?.issues || []).some((issue) => {
    return (
      STOCK_REFRESH_CODES.has(normalizeUpper(issue?.code)) ||
      STOCK_REFRESH_STATUSES.has(normalizeLower(issue?.stockStatus))
    );
  });
}

async function refreshAfterFailure(dispatch, payload) {
  if (!shouldRefreshAfterFailure(payload)) return;

  try {
    const { data } = await api.post(`${CART_BASE_URL}/refresh`);

    dispatch({
      type: actionTypes.CART_REFRESH_SUCCESS,
      payload: data,
      meta: {
        silent: true,
        preserveError: true,
        preserveMeta: true,
      },
    });
  } catch {
    // Best-effort basket sync only. Keep the original error.
  }
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function buildAddKey({
  productId = "",
  variantSku = "",
  variantType = "",
} = {}) {
  return [
    safeText(productId),
    normalizeUpper(variantSku),
    normalizeLower(variantType),
  ].join("::");
}

function buildAddPayload({
  productId,
  variantSku = "",
  variantType = "",
  qty = 1,
} = {}) {
  return {
    productId: safeText(productId),
    variantSku: normalizeUpper(variantSku),
    variantType: normalizeLower(variantType),
    qty: normalizeQty(qty),
  };
}

function normalizeCountry(value = "", fallback = "") {
  const country = normalizeUpper(value);

  return country || normalizeUpper(fallback);
}

function normalizeAddressPayload(address = {}, { defaultCountry = "" } = {}) {
  return {
    fullName: safeText(address?.fullName),
    company: safeText(address?.company),
    line1: safeText(address?.line1),
    line2: safeText(address?.line2),
    city: safeText(address?.city),
    state: safeText(address?.state),
    postalCode: safeText(address?.postalCode),
    country: normalizeCountry(address?.country, defaultCountry),
    phone: safeText(address?.phone),
  };
}

function normalizeFulfillmentMethod(value = "delivery") {
  const method = normalizeLower(value);

  return VALID_FULFILLMENT_METHODS.has(method) ? method : "delivery";
}

function normalizePaymentMethod(value = "") {
  const method = normalizeLower(value);

  return VALID_PAYMENT_METHODS.has(method) ? method : "";
}

/* -------------------------------------------------------------------------- */
/*                                  Resets                                    */
/* -------------------------------------------------------------------------- */

export const resetCartError = () => ({
  type: actionTypes.CART_RESET_ERROR,
});

export const resetCartMeta = () => ({
  type: actionTypes.CART_RESET_META,
});

export const resetCartState = () => ({
  type: actionTypes.CART_RESET_STATE,
});

export const resetCheckoutQuote = () => ({
  type: actionTypes.CART_QUOTE_RESET,
});

/* -------------------------------------------------------------------------- */
/*                                   Reads                                    */
/* -------------------------------------------------------------------------- */

export const getCart = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_GET_REQUEST });

    const { data } = await api.get(CART_BASE_URL);

    dispatch({
      type: actionTypes.CART_GET_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not load your basket right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_GET_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

export const getCartSummary = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_SUMMARY_REQUEST });

    const { data } = await api.get(`${CART_BASE_URL}/summary`);

    dispatch({
      type: actionTypes.CART_SUMMARY_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not load your basket summary right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_SUMMARY_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

export const validateCart = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_VALIDATE_REQUEST });

    const { data } = await api.get(`${CART_BASE_URL}/validate`);

    dispatch({
      type: actionTypes.CART_VALIDATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not check your basket right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_VALIDATE_FAIL,
      payload: errorPayload,
    });

    await refreshAfterFailure(dispatch, errorPayload);

    throw errorPayload;
  }
};

export const getCheckoutQuote =
  (params = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: actionTypes.CART_QUOTE_REQUEST });

      const { data } = await api.get(`${CART_BASE_URL}/quote`, {
        params: cleanParams(params),
      });

      dispatch({
        type: actionTypes.CART_QUOTE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not update your checkout totals right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_QUOTE_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const refreshCart = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_REFRESH_REQUEST });

    const { data } = await api.post(`${CART_BASE_URL}/refresh`);

    dispatch({
      type: actionTypes.CART_REFRESH_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not refresh your basket right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_REFRESH_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

/* -------------------------------------------------------------------------- */
/*                                Mutations                                   */
/* -------------------------------------------------------------------------- */

export const addToCart =
  ({ productId, variantSku = "", variantType = "", qty = 1 } = {}) =>
  async (dispatch) => {
    const requestPayload = buildAddPayload({
      productId,
      variantSku,
      variantType,
      qty,
    });

    const addKey = buildAddKey(requestPayload);

    try {
      dispatch({
        type: actionTypes.CART_ADD_ITEM_REQUEST,
        payload: requestPayload,
        meta: {
          addKey,
          ...requestPayload,
        },
      });

      if (!requestPayload.productId) {
        throw buildClientValidationError(
          "Product id is required.",
          "PRODUCT_ID_REQUIRED",
        );
      }

      const { data } = await api.post(`${CART_BASE_URL}/items`, requestPayload);

      dispatch({
        type: actionTypes.CART_ADD_ITEM_SUCCESS,
        payload: data,
        meta: {
          addKey,
          ...requestPayload,
        },
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not add that item to your basket right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_ADD_ITEM_FAIL,
        payload: errorPayload,
        meta: {
          addKey,
          ...requestPayload,
        },
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

export const setCartItemQty =
  ({ itemId, qty } = {}) =>
  async (dispatch) => {
    const safeItemId = safeText(itemId);

    const meta = {
      itemId: safeItemId,
      qty: normalizeQty(qty),
    };

    try {
      dispatch({
        type: actionTypes.CART_SET_ITEM_QTY_REQUEST,
        payload: meta,
        meta,
      });

      if (!safeItemId) {
        throw buildClientValidationError(
          "Basket item id is required.",
          "CART_ITEM_ID_REQUIRED",
        );
      }

      const { data } = await api.put(
        `${CART_BASE_URL}/items/${encodePath(safeItemId)}`,
        {
          qty: meta.qty,
        },
      );

      dispatch({
        type: actionTypes.CART_SET_ITEM_QTY_SUCCESS,
        payload: data,
        meta,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not update that item quantity right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SET_ITEM_QTY_FAIL,
        payload: errorPayload,
        meta,
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

export const incrementCartItem =
  (itemId = "") =>
  async (dispatch) => {
    const safeItemId = safeText(itemId);
    const meta = { itemId: safeItemId };

    try {
      dispatch({
        type: actionTypes.CART_INCREMENT_ITEM_REQUEST,
        payload: meta,
        meta,
      });

      if (!safeItemId) {
        throw buildClientValidationError(
          "Basket item id is required.",
          "CART_ITEM_ID_REQUIRED",
        );
      }

      const { data } = await api.patch(
        `${CART_BASE_URL}/items/${encodePath(safeItemId)}/increment`,
      );

      dispatch({
        type: actionTypes.CART_INCREMENT_ITEM_SUCCESS,
        payload: data,
        meta,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not increase that item quantity right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_INCREMENT_ITEM_FAIL,
        payload: errorPayload,
        meta,
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

export const decrementCartItem =
  (itemId = "") =>
  async (dispatch) => {
    const safeItemId = safeText(itemId);
    const meta = { itemId: safeItemId };

    try {
      dispatch({
        type: actionTypes.CART_DECREMENT_ITEM_REQUEST,
        payload: meta,
        meta,
      });

      if (!safeItemId) {
        throw buildClientValidationError(
          "Basket item id is required.",
          "CART_ITEM_ID_REQUIRED",
        );
      }

      const { data } = await api.patch(
        `${CART_BASE_URL}/items/${encodePath(safeItemId)}/decrement`,
      );

      dispatch({
        type: actionTypes.CART_DECREMENT_ITEM_SUCCESS,
        payload: data,
        meta,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not decrease that item quantity right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_DECREMENT_ITEM_FAIL,
        payload: errorPayload,
        meta,
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

export const removeCartItem =
  (itemId = "") =>
  async (dispatch) => {
    const safeItemId = safeText(itemId);
    const meta = { itemId: safeItemId };

    try {
      dispatch({
        type: actionTypes.CART_REMOVE_ITEM_REQUEST,
        payload: meta,
        meta,
      });

      if (!safeItemId) {
        throw buildClientValidationError(
          "Basket item id is required.",
          "CART_ITEM_ID_REQUIRED",
        );
      }

      const { data } = await api.delete(
        `${CART_BASE_URL}/items/${encodePath(safeItemId)}`,
      );

      dispatch({
        type: actionTypes.CART_REMOVE_ITEM_SUCCESS,
        payload: data,
        meta,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not remove that item from your basket right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_REMOVE_ITEM_FAIL,
        payload: errorPayload,
        meta,
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

export const clearCart = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_CLEAR_REQUEST });

    const { data } = await api.delete(CART_BASE_URL);

    dispatch({
      type: actionTypes.CART_CLEAR_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not clear your basket right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_CLEAR_FAIL,
      payload: errorPayload,
    });

    await refreshAfterFailure(dispatch, errorPayload);

    throw errorPayload;
  }
};

export const replaceCart =
  (items = []) =>
  async (dispatch) => {
    const payloadItems = Array.isArray(items)
      ? items
          .map((item) => ({
            productId: safeText(item?.productId),
            variantSku: normalizeUpper(item?.variantSku),
            variantType: normalizeLower(item?.variantType),
            qty: normalizeQty(item?.qty),
          }))
          .filter((item) => item.productId)
      : [];

    try {
      dispatch({
        type: actionTypes.CART_REPLACE_REQUEST,
        payload: { items: payloadItems },
      });

      const { data } = await api.put(CART_BASE_URL, { items: payloadItems });

      dispatch({
        type: actionTypes.CART_REPLACE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not update your basket right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_REPLACE_FAIL,
        payload: errorPayload,
      });

      await refreshAfterFailure(dispatch, errorPayload);

      throw errorPayload;
    }
  };

/* -------------------------------------------------------------------------- */
/*                                  Cleanup                                   */
/* -------------------------------------------------------------------------- */

export const removeUnavailableItems = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_REMOVE_UNAVAILABLE_REQUEST });

    const { data } = await api.post(`${CART_BASE_URL}/remove-unavailable`);

    dispatch({
      type: actionTypes.CART_REMOVE_UNAVAILABLE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not remove unavailable items right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_REMOVE_UNAVAILABLE_FAIL,
      payload: errorPayload,
    });

    await refreshAfterFailure(dispatch, errorPayload);

    throw errorPayload;
  }
};

export const removePriceChangedItems = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_REMOVE_PRICE_CHANGED_REQUEST });

    const { data } = await api.post(`${CART_BASE_URL}/remove-price-changed`);

    dispatch({
      type: actionTypes.CART_REMOVE_PRICE_CHANGED_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not remove price-changed items right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_REMOVE_PRICE_CHANGED_FAIL,
      payload: errorPayload,
    });

    await refreshAfterFailure(dispatch, errorPayload);

    throw errorPayload;
  }
};

export const mergeGuestCart = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_MERGE_REQUEST });

    const { data } = await api.post(`${CART_BASE_URL}/merge`);

    dispatch({
      type: actionTypes.CART_MERGE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not merge your basket right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_MERGE_FAIL,
      payload: errorPayload,
    });

    await refreshAfterFailure(dispatch, errorPayload);

    throw errorPayload;
  }
};

export const applyPromoCode =
  (code = "") =>
  async (dispatch) => {
    const safeCode = normalizeUpper(code);

    try {
      dispatch({
        type: actionTypes.CART_APPLY_PROMO_CODE_REQUEST,
        payload: { code: safeCode },
      });

      if (!safeCode) {
        throw buildClientValidationError(
          "Enter a promo code.",
          "PROMO_CODE_REQUIRED",
        );
      }

      const { data } = await api.post(`${CART_BASE_URL}/promo-code`, {
        code: safeCode,
      });

      dispatch({
        type: actionTypes.CART_APPLY_PROMO_CODE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not apply that promo code right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_APPLY_PROMO_CODE_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const removePromoCode = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_REMOVE_PROMO_CODE_REQUEST });

    const { data } = await api.delete(`${CART_BASE_URL}/promo-code`);

    dispatch({
      type: actionTypes.CART_REMOVE_PROMO_CODE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not remove that promo code right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_REMOVE_PROMO_CODE_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

/* -------------------------------------------------------------------------- */
/*                             Checkout draft state                           */
/* -------------------------------------------------------------------------- */

export const getCheckout = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_GET_CHECKOUT_REQUEST });

    const { data } = await api.get(`${CART_BASE_URL}/checkout`);

    dispatch({
      type: actionTypes.CART_GET_CHECKOUT_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not load checkout right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_GET_CHECKOUT_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

export const saveFulfillmentMethod =
  (fulfillmentMethod = "delivery") =>
  async (dispatch) => {
    const payload = {
      fulfillmentMethod: normalizeFulfillmentMethod(fulfillmentMethod),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_FULFILLMENT_METHOD_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/fulfillment`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_FULFILLMENT_METHOD_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your fulfilment method right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_FULFILLMENT_METHOD_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const saveCustomerDetails =
  (customer = {}) =>
  async (dispatch) => {
    const payload = {
      name: safeText(customer?.name),
      email: normalizeLower(customer?.email),
      phone: safeText(customer?.phone),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_CUSTOMER_DETAILS_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/customer`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_CUSTOMER_DETAILS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your contact details right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_CUSTOMER_DETAILS_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const saveShippingAddress =
  (shippingAddress = {}) =>
  async (dispatch) => {
    const payload = normalizeAddressPayload(shippingAddress);

    try {
      dispatch({
        type: actionTypes.CART_SAVE_SHIPPING_ADDRESS_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/shipping-address`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_SHIPPING_ADDRESS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your shipping address right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_SHIPPING_ADDRESS_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const savePickupDetails =
  (pickupDetails = {}) =>
  async (dispatch) => {
    const payload = {
      appointmentOnly:
        typeof pickupDetails?.appointmentOnly === "boolean"
          ? pickupDetails.appointmentOnly
          : true,
      date: safeText(pickupDetails?.date),
      dayLabel: safeText(pickupDetails?.dayLabel),
      timeSlot: safeText(pickupDetails?.timeSlot),
      reference: safeText(pickupDetails?.reference),
      instructions: safeText(pickupDetails?.instructions),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_PICKUP_DETAILS_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/pickup-details`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_PICKUP_DETAILS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your pickup details right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_PICKUP_DETAILS_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const saveBillingOptions =
  (billing = {}) =>
  async (dispatch) => {
    const payload = {
      useShippingAsBilling:
        typeof billing?.useShippingAsBilling === "boolean"
          ? billing.useShippingAsBilling
          : true,
      address: normalizeAddressPayload(billing?.address || {}, {
        defaultCountry: "GB",
      }),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_BILLING_OPTIONS_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/billing`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_BILLING_OPTIONS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your billing options right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_BILLING_OPTIONS_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const saveCheckoutAcknowledgements =
  (acknowledgements = {}) =>
  async (dispatch) => {
    const payload = {
      textileNoReturnAccepted: Boolean(
        acknowledgements?.textileNoReturnAccepted,
      ),
      standardReturnsAccepted: Boolean(
        acknowledgements?.standardReturnsAccepted,
      ),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/acknowledgements`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your checkout acknowledgements right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const savePaymentMethod =
  (paymentMethod = "") =>
  async (dispatch) => {
    const payload = {
      paymentMethod: normalizePaymentMethod(paymentMethod),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_PAYMENT_METHOD_REQUEST,
        payload,
      });

      if (!payload.paymentMethod) {
        throw buildClientValidationError(
          'Payment method must be "stripe" or "paypal".',
          "PAYMENT_METHOD_REQUIRED",
        );
      }

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/payment`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_PAYMENT_METHOD_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your payment method right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_PAYMENT_METHOD_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const saveCheckoutNotes =
  (notes = "") =>
  async (dispatch) => {
    const payload = {
      notes: safeText(notes).slice(0, 1000),
    };

    try {
      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_NOTES_REQUEST,
        payload,
      });

      const { data } = await api.put(
        `${CART_BASE_URL}/checkout/notes`,
        payload,
      );

      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_NOTES_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const errorPayload = normalizeErrorPayload(
        error,
        "We could not save your checkout notes right now. Please try again later.",
      );

      dispatch({
        type: actionTypes.CART_SAVE_CHECKOUT_NOTES_FAIL,
        payload: errorPayload,
      });

      throw errorPayload;
    }
  };

export const resetCheckout = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CART_RESET_CHECKOUT_REQUEST });

    const { data } = await api.delete(`${CART_BASE_URL}/checkout`);

    dispatch({
      type: actionTypes.CART_RESET_CHECKOUT_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = normalizeErrorPayload(
      error,
      "We could not reset checkout right now. Please try again later.",
    );

    dispatch({
      type: actionTypes.CART_RESET_CHECKOUT_FAIL,
      payload: errorPayload,
    });

    throw errorPayload;
  }
};

/* -------------------------------------------------------------------------- */
/*                              Checkout helper                               */
/* -------------------------------------------------------------------------- */

export const prepareCartForCheckout = () => async (dispatch) => {
  const validation = await dispatch(validateCart());

  if (validation?.ok === false) {
    const errorPayload = normalizeErrorPayload(
      buildClientValidationError(
        validation?.message || "Basket is not ready for checkout.",
        validation?.empty ? "CART_EMPTY" : "CART_NOT_READY",
        409,
        validation?.issues || [],
      ),
      "Basket is not ready for checkout.",
    );

    throw errorPayload;
  }

  return dispatch(refreshCart());
};
